import React, { useMemo, useState, useEffect } from 'react'
import AsyncSelect from 'react-select/async'
import { includes } from 'lodash'
import { Form } from 'react-bootstrap'
import './SelectWithFilter.css'

const SelectWithFilter = ({ name, frontEnd = true, ...props }) => {
    const {
        id = name,
        label,
        options = [],
        value,
        error = false,
        loading,
        wrapperClassName,
        className,
        classNamePrefix = undefined,
        placeholder = 'Enter search term(s)',
        disabled,
        defaultIfOneOption = true,
        debug = false,
        onTermChange,
        onChange,
        isClearable = true,
        optionFilterCheck = ({ term, option: { label, value, option } }) => {
            return (
                includes(label.toLowerCase(), term.toLowerCase()) ||
                includes(value.toLowerCase(), term.toLowerCase()) ||
                includes(option.id, term.toLowerCase())
            )
        },
        getOption = (option) => {
            const { label, value } = option

            return ({
                value,
                label,
                option
            })
        }
    } = props

    const [_defaultIfOneOption, setDefaultIfOneOption] = useState(defaultIfOneOption)

    const [menuIsOpen, setMenuIsOpen] = useState(false)

    const formattedOptions = useMemo(() => {
        return options.map((option) => getOption(option))
    }, [options])

    const filterOptions = (search = '') => {
        if (!search.trim().length) {
            return formattedOptions
        }
        const searchTerm = search.toLowerCase()

        return formattedOptions.filter((option) => optionFilterCheck({
            term: searchTerm,
            option
        }))
    }

    const loadOptions = (inputValue, callback) => {
        if (onTermChange) {
            onTermChange(inputValue, callback, getOption)
        } else {
            callback(filterOptions(inputValue))
        }
    }

    useEffect(() => {
        loadOptions(value)
    }, [])

    const getTheme = (theme) => ({
        ...theme,
        borderRadius: 0,

        // TODO:: does not work probably using the wrong prop
        control: {
            height: 48
        },

        spacing: {
            ...theme.spacing,
            controlHeight: 49
        }
    })

    const getLabelForValue = (value) => {
        const found_option = formattedOptions.find((option) => option.value == value) || {}

        debug && console.log('base.getLabelForValue', { found_option })

        return found_option.label || ''
    }

    /**
     * this method is triggered many times. check to make sure the value has changed before triggering again.
     *
     * @param label
     * @param value
     * @param option
     */
    const handleOnChange = (e) => {
        const { label = '', value = '', option = {} } = e || {}
        debug && console.log('base.handleOnChange', { label, value, option })
        if (value !== props.value) {
            debug && console.log('base.handleOnChange.calling.onChange')
            onChange({
                target: {
                    name,
                    value: value || '',
                    label: label || '',
                    option: option || {}
                }
            })
        }

        if (option.entries && option.entries > 1) {
            setMenuIsOpen(true)
        } else {
            setMenuIsOpen(false)
        }
    }

    const setFirstOption = () => {
        if (_defaultIfOneOption !== true || options.length != 1) {
            return false
        }

        setDefaultIfOneOption(false)

        const {
            value: singleValue = '',
        } = getOption(options[0]) || {}

        onChange({ target: { name, value: singleValue } })
    }

    const selectValue = useMemo(() => {
        if (value) {
            const labelForValue = getLabelForValue(value)
            return {
                label: labelForValue ? labelForValue : value, // Fallback to the value if no label is found
                value,
            }
        }
        return ''
    }, [value, options])

    return (
        <div className={`${!frontEnd ? 'form-group' : ''} ${wrapperClassName}`} id={`${id}-wrapper`}>
            {label && <label htmlFor={id} className={`control-label label-for-${name}`}>{label}</label>}
            <AsyncSelect
                onKeyDown={props.onKeyDown || {}}
                id={id}
                placeholder={placeholder}
                name={name}
                classNamePrefix={classNamePrefix}
                className={className}
                loadOptions={loadOptions}
                defaultOptions={value && formattedOptions}
                cacheOptions={false}
                value={selectValue}
                theme={getTheme}
                onChange={handleOnChange}
                isDisabled={disabled}
                isClearable={isClearable}
                isLoading={loading}
                classNames={{ control: () => error && "is-invalid" }}
                menuIsOpen={menuIsOpen}
                onMenuOpen={() => setMenuIsOpen(true)}
                onMenuClose={() => setMenuIsOpen(false)}
                styles={{
                    control: (base, state) => ({
                        ...base,
                        backgroundColor: disabled && '#eeeeee',
                        border: error && frontEnd ? '1px solid #dc3545' : '1px solid #ced4da',
                        padding: '0 0.14rem',
                        height: 50
                    }),
                    placeholder: (base) => ({
                        ...base,
                        color: '#495057',
                    }),
                    dropdownIndicator: (base) => ({
                        ...base,
                        color: '#495057 !important'
                    }),
                    indicatorSeparator: () => ({ display: 'none' })
                }}
            />
            {frontEnd ? (
                error && <p className='parsley-errors-list filled'>{error}</p>
            ) : (
                <Form.Control.Feedback type="is-invalid" className={feedbackClassName}>
                    {error}
                </Form.Control.Feedback>
            )}
        </div>
    )
}

export default SelectWithFilter
